import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react'
import { Alert, Box, CloseButton, Portal } from '@chakra-ui/react'

const NotificationShowTime = 5000

export interface Notification {
    message: string
    type: 'info' | 'warning' | 'error'
}

export const NotificationContext = createContext<{
    notification: Notification | undefined
    setNotification: (notification: Notification) => void
}>({
    notification: undefined,
    setNotification: () => {}
})

const statusTranslation: Record<Notification['type'], 'success' | 'warning' | 'error'> = {
    info: 'success',
    warning: 'warning',
    error: 'error'
}

export const NotifcationProvider = ({ children }: { children: ReactNode }) => {
    const [notification, setNotification] = useState<Notification | undefined>()

    return (
        <NotificationContext.Provider
            value={{
                notification,
                setNotification
            }}
        >
            {children}
        </NotificationContext.Provider>
    )
}

export const NotificationRenderer = () => {
    const { notification } = useContext(NotificationContext)

    const [showNotification, setShowNotification] = useState<Notification | undefined>()

    useEffect(() => {
        setShowNotification(notification)

        let timeout: ReturnType<typeof setTimeout> | undefined
        if (notification) {
            timeout = setTimeout(() => setShowNotification(undefined), NotificationShowTime)
        }

        return () => {
            if (timeout) {
                clearTimeout(timeout)
            }
        }
    }, [notification])

    if (!showNotification) {
        return null
    }

    return (
        <Portal>
            <Box
                position="fixed"
                bottom={4}
                left={0}
                right={0}
                display="flex"
                justifyContent="center"
                zIndex={1400}
                px={4}
            >
                <Alert.Root
                    status={statusTranslation[showNotification.type]}
                    maxW="md"
                    borderRadius="md"
                    boxShadow="lg"
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>{showNotification.message}</Alert.Title>
                    </Alert.Content>
                    <CloseButton
                        alignSelf="flex-start"
                        position="relative"
                        top="-1"
                        onClick={() => setShowNotification(undefined)}
                    />
                </Alert.Root>
            </Box>
        </Portal>
    )
}

export const useNotification = () => {
    const ctx = useContext(NotificationContext)
    return ctx.setNotification
}
